'use client';

import { useTranslations } from 'next-intl';
import { useMounted } from '@/hooks/useMounted';
import { ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis } from 'recharts';
import type { PlayerStanding } from '@/draw/types';
import { standingShortName } from '../shortName';
import { SectionLabel } from './SectionLabel';

interface RadarChartCardProps {
  standings: PlayerStanding[];
  isDark?: boolean;
}

const RADAR_COLORS_DARK = ['#FBBF24', '#F59E0B', '#93a1ad'];
const RADAR_COLORS_LIGHT = ['#1B2D4F', '#F5A623', '#8B95A5'];

export function RadarChartCard({ standings, isDark = true }: RadarChartCardProps) {
  const t = useTranslations('Celebration');
  const mounted = useMounted();
  const radarColors = isDark ? RADAR_COLORS_DARK : RADAR_COLORS_LIGHT;

  const top3 = standings.slice(0, 3);
  const maxWins = Math.max(...standings.map((p) => p.wins), 1);
  const maxGames = Math.max(...standings.map((p) => p.gamesWon), 1);
  const maxPtsFor = Math.max(...standings.map((p) => p.gamePointsFor), 1);
  const maxPtsAgainst = Math.max(...standings.map((p) => p.gamePointsAgainst), 1);

  const shortName = (p: PlayerStanding) => standingShortName(p);

  const data = [
    {
      stat: t('wins'),
      ...Object.fromEntries(top3.map((p) => [shortName(p), (p.wins / maxWins) * 100])),
    },
    {
      stat: t('games'),
      ...Object.fromEntries(top3.map((p) => [shortName(p), (p.gamesWon / maxGames) * 100])),
    },
    {
      stat: t('pointsFor'),
      ...Object.fromEntries(top3.map((p) => [shortName(p), (p.gamePointsFor / maxPtsFor) * 100])),
    },
    {
      stat: 'Defense',
      ...Object.fromEntries(
        top3.map((p) => [
          shortName(p),
          Math.max(0, 100 - (p.gamePointsAgainst / maxPtsAgainst) * 100),
        ]),
      ),
    },
  ];

  const gridColor = isDark ? 'rgba(255,255,255,0.1)' : '#E8E2D4';
  const tickColor = isDark ? 'rgba(255,255,255,0.4)' : '#1B2D4F80';

  return (
    <>
      <SectionLabel>{t('playerComparison')}</SectionLabel>
      {mounted && (
        <ResponsiveContainer width="100%" height={240}>
          <RadarChart data={data}>
            <PolarGrid stroke={gridColor} />
            <PolarAngleAxis dataKey="stat" tick={{ fill: tickColor, fontSize: 11 }} />
            {top3.map((p, i) => (
              <Radar
                key={p.playerId}
                name={shortName(p)}
                dataKey={shortName(p)}
                stroke={radarColors[i]}
                fill={radarColors[i]}
                fillOpacity={i === 0 ? 0.15 : i === 1 ? 0.08 : 0.05}
                strokeWidth={i === 0 ? 2 : i === 1 ? 1.5 : 1}
              />
            ))}
          </RadarChart>
        </ResponsiveContainer>
      )}
      <div className="mt-2 flex justify-center gap-4">
        {top3.map((p, i) => (
          <div key={p.playerId} className="flex items-center gap-1.5 text-[11px] text-[#8B95A5]">
            <div className="h-2 w-2 rounded-full" style={{ background: radarColors[i] }} />
            {shortName(p)}
          </div>
        ))}
      </div>
    </>
  );
}
